import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useLocale } from "../contexts/LocaleContext";
import "./PhotoEditor.css";

const DEFAULT_CANVAS_SIZE = 400;
const DEFAULT_OUTPUT_SIZE = 1000;

export interface PhotoEditorRef {
  exportCroppedJpeg: () => Promise<string | null>;
}

export interface PhotoEditorProps {
  source: File | string | null;
  canvasSize?: number;
  outputSize?: number;
  className?: string;
  onEdit?: () => void;
}

/**
 * Square crop viewport with zoom, pan, and 90° rotation — shared by cart modal
 * and profile popup.
 */
export const PhotoEditor = forwardRef<PhotoEditorRef, PhotoEditorProps>(
  function PhotoEditor(
    {
      source,
      canvasSize = DEFAULT_CANVAS_SIZE,
      outputSize = DEFAULT_OUTPUT_SIZE,
      className,
      onEdit,
    },
    ref,
  ) {
    const { t } = useLocale();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [scale, setScale] = useState(1);
    const [offsetX, setOffsetX] = useState(0);
    const [offsetY, setOffsetY] = useState(0);
    const [rotation, setRotation] = useState(0);
    const dragging = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });

    useEffect(() => {
      if (!source) {
        setImage(null);
        return;
      }

      let objUrl: string | undefined;
      const img = new Image();
      setImage(null);
      img.onload = () => {
        setImage(img);
        const aspect = img.width / img.height;
        const base =
          aspect > 1 ? canvasSize / img.height : canvasSize / img.width;
        setScale(Math.min(Math.max(base, 0.1), 3));
        setOffsetX(0);
        setOffsetY(0);
        setRotation(0);
      };

      if (typeof source === "string") {
        img.src = source;
      } else {
        objUrl = URL.createObjectURL(source);
        img.src = objUrl;
      }

      return () => {
        img.onload = null;
        if (objUrl) URL.revokeObjectURL(objUrl);
      };
    }, [source, canvasSize]);

    const drawPreview = useCallback(() => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      const img = image;
      if (!canvas || !ctx || !img) return;

      ctx.clearRect(0, 0, canvasSize, canvasSize);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const cx = offsetX + scaledWidth / 2;
      const cy = offsetY + scaledHeight / 2;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(
        img,
        -scaledWidth / 2,
        -scaledHeight / 2,
        scaledWidth,
        scaledHeight,
      );
      ctx.restore();

      ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, canvasSize, canvasSize);
    }, [image, scale, offsetX, offsetY, rotation, canvasSize]);

    useEffect(() => {
      drawPreview();
    }, [drawPreview]);

    const notifyEdit = useCallback(() => {
      onEdit?.();
    }, [onEdit]);

    const pointerPositionInCanvas = useCallback(
      (clientX: number, clientY: number) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;

        // The canvas is rendered responsively on mobile. Pointer coordinates are
        // CSS pixels, while offsets are stored in the canvas' internal 400x400
        // coordinate system. Convert between the two so dragging stays 1:1 on
        // Android/iOS even when the canvas is visually scaled down.
        return {
          x: (clientX - rect.left) * (canvas.width / rect.width),
          y: (clientY - rect.top) * (canvas.height / rect.height),
        };
      },
      [],
    );

    const onPointerMove = useCallback(
      (clientX: number, clientY: number) => {
        if (!dragging.current) return;
        const pos = pointerPositionInCanvas(clientX, clientY);
        if (!pos) return;

        setOffsetX((ox) => ox + pos.x - lastPos.current.x);
        setOffsetY((oy) => oy + pos.y - lastPos.current.y);
        lastPos.current = pos;
        notifyEdit();
      },
      [notifyEdit, pointerPositionInCanvas],
    );

    const rotateBy = (delta: number) => {
      setRotation((r) => (r + delta + 360) % 360);
      notifyEdit();
    };

    useImperativeHandle(
      ref,
      () => ({
        exportCroppedJpeg: () =>
          new Promise((resolve) => {
            const canvas = canvasRef.current;
            if (!canvas || !image) {
              resolve(null);
              return;
            }

            const output = document.createElement("canvas");
            output.width = outputSize;
            output.height = outputSize;
            const octx = output.getContext("2d");
            if (!octx) {
              resolve(null);
              return;
            }

            octx.drawImage(
              canvas,
              0,
              0,
              canvasSize,
              canvasSize,
              0,
              0,
              outputSize,
              outputSize,
            );

            output.toBlob(
              (blob) => {
                if (!blob) {
                  resolve(null);
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
              },
              "image/jpeg",
              0.9,
            );
          }),
      }),
      [canvasSize, image, outputSize],
    );

    if (!source) return null;

    return (
      <div className={["photo-editor", className].filter(Boolean).join(" ")}>
        <div className="photo-editor-crop-wrap">
          <canvas
            ref={canvasRef}
            className="photo-editor-canvas"
            width={canvasSize}
            height={canvasSize}
            onPointerDown={(e) => {
              if (e.pointerType === "mouse" && e.button !== 0) return;
              const pos = pointerPositionInCanvas(e.clientX, e.clientY);
              if (!pos) return;

              dragging.current = true;
              lastPos.current = pos;
              e.currentTarget.setPointerCapture?.(e.pointerId);
              e.preventDefault();
            }}
            onPointerMove={(e) => {
              if (!dragging.current) return;
              onPointerMove(e.clientX, e.clientY);
              e.preventDefault();
            }}
            onPointerUp={(e) => {
              dragging.current = false;
              if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
                e.currentTarget.releasePointerCapture?.(e.pointerId);
              }
            }}
            onPointerCancel={() => {
              dragging.current = false;
            }}
            onLostPointerCapture={() => {
              dragging.current = false;
            }}
          />
        </div>
        <div className="photo-editor-controls">
          <label htmlFor="photo-editor-scale-slider">{t("photoEditor.zoom")}</label>
          <input
            id="photo-editor-scale-slider"
            type="range"
            min={0.1}
            max={3}
            step={0.05}
            value={scale}
            onChange={(ev) => {
              setScale(parseFloat(ev.target.value));
              notifyEdit();
            }}
          />
          <div className="photo-editor-rotate">
            <button
              type="button"
              className="photo-editor-rotate-btn"
              aria-label={t("photoEditor.rotateCCW")}
              title={t("photoEditor.rotateCCW")}
              onClick={() => rotateBy(-90)}
            >
              ↺
            </button>
            <button
              type="button"
              className="photo-editor-rotate-btn"
              aria-label={t("photoEditor.rotateCW")}
              title={t("photoEditor.rotateCW")}
              onClick={() => rotateBy(90)}
            >
              ↻
            </button>
          </div>
        </div>
      </div>
    );
  },
);
